import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../../components/base/Dialog/Dialog';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import ApplicationsDialogDTO from '../../../dto/ApplicationsDialogDTO';
import { loadReasonListAll } from '../../ReferenceInformation/actions';
import { getReferenceInformationData } from '../../ReferenceInformation/reducer';
import { CHECK_STATUS, REASON_TYPE } from '../../../configs';
import CustomSelectInputWithHelper from '../../../components/fields/SelectInput/CustomSelectInputWithHelper';
import CheckBoxInputWithLabel from '../../../components/fields/CheckBoxInput/CheckBoxInputWithLabel';
import { applicationFieldsValidation } from '../actions';
import { showGlobalConfirm } from '../../System/actions';
import { infoFormatterIntl, renderValidationMessage } from '../../../utils/formatters';
import { responseLogWithNotification } from '../../../utils/utils';
import TimerLoader from '../../../components/base/Loader/TimerLoader';

export default function ApplicationDenied(props) {
  const {
    close = () => {},
    send = () => {},
    procedure = {},
    legal_entity_type = '',
    buttonExecText = 'Отказать',
    currentApplication,
    withoutValidation = false,
  } = props;
  const onSend = () => {
    const values = getValues();
    send(values);
  };

  const {
    control, watch, setValue, getValues,
  } = useForm({
    defaultValues: {
      rejection_reason_ids: [],
    },
  });
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const DTO = new ApplicationsDialogDTO();
  const attributes = DTO.getAttributesByName();
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [results, setResult] = useState([]);
  const [selected, setSelected] = useState([]);

  const handleValidateAndAccept = () => {
    close();
    if (withoutValidation) {
      onSend();
      return;
    }
    dispatch(applicationFieldsValidation({ id: currentApplication?.id })).then(data => {
      const checkErrors = data?.checks || {};
      const errors = data?.errors || {};
      const checkErrorsKeys = Object.keys(checkErrors);
      const errorsKeys = Object.keys(errors);
      if (Object.values(checkErrors).filter(f => f.status !== 2).length + errorsKeys.length) {
        dispatch(showGlobalConfirm({
          confirmTitle: 'Обращение содержит ошибки в полях',
          confirmMessage: renderValidationMessage(errors, checkErrors, checkErrorsKeys, errorsKeys),
          confirmButtonText: 'Отказать',
          confirmClassName: 'remove-preset',
        })).then(() => {
          onSend();
        }).catch(() => {});
      } else {
        onSend();
      }
    }).catch(responseLogWithNotification);
  };

  const {
    reasonListAll,
    loadingReasonListAll,
  } = useSelector(getReferenceInformationData);

  const loadFieldsValidation = () => {
    dispatch(applicationFieldsValidation({
      id: currentApplication?.id,
      filter: !withoutValidation ? {
        reasons_types: [REASON_TYPE.APPLICATION_DENIED],
        procedures: [procedure],
        legal_entity_types: [legal_entity_type],
      } : {},
    })).then(data => {
      const { checks = {}, rejection_reasons = {}, errors = {} } = data;
      const getIds = (key, e) => (rejection_reasons[key]?.length ? rejection_reasons[key].map(el => el?.id) : e?.id ? [e.id] : []);
      const checks_value = Object.entries(checks).flatMap(([key, values]) => values?.errors.map(e => ({ ...e, key, ids: rejection_reasons[key]?.map(el => el?.id) ?? [] })));
      const errors_value = Object.entries(errors).flatMap(([key, values]) => values.map(e => ({
        ids: getIds(key, e), status: CHECK_STATUS.FAILED, message: e, key,
      })));

      setResult([...errors_value, ...checks_value]);
    });
  };

  const additionalFilter = withoutValidation ? {} : { reasons_types: [REASON_TYPE.APPLICATION_DENIED], procedures: [procedure], legal_entity_types: [legal_entity_type] };

  const getDefaultOptions = () => {
    const currentIds = getValues('rejection_reason_ids');
    return reasonListAll?.filter(e => currentIds.includes(e.id)).map(e => ({ name: e?.name, value: e?.id })) || [];
  };

  const removeSelected = id => {
    setSelected(selected.filter(item => !item.ids?.includes(id)));
  };

  const addSelected = id => {
    setSelected([...selected, ...results.filter(item => item.ids?.includes(id))]);
  };

  useEffect(() => { if (!withoutValidation) { loadFieldsValidation(); } }, []);

  useEffect(() => {
    const subscription = watch(values => {
      if (values && Array.isArray(values?.rejection_reason_ids) && values?.rejection_reason_ids?.length) {
        setRejectionReasons(values.rejection_reason_ids);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    setValue('rejection_reason_note', reasonListAll.filter(e => rejectionReasons.includes(e.id)).map(e => e.message).join(', '));
  }, [rejectionReasons]);

  return (
    <Dialog
      className="application-action-dialog"
      onClose={close}
      open
    >
      <DialogTitle header="Отказ" title={<Icons onClick={close} customIcon icon="close" className="close" />} />
      <DialogContent className="application-action-content">
        <TimerLoader loading={loadingReasonListAll} />
        <div className="application-action-content-left">
          <div className="f3">Форма выбора причины отказа</div>
          <Row>
            <FieldDTOWithCol
              control={control}
              component={CustomSelectInputWithHelper}
              dtoObject={attributes.rejection_reason_ids}
              variant="material"
              multiple
              withLabel
              optionFormatter={item => ({ value: item.id, name: item.name })}
              loadAction={loadReasonListAll}
              additionalFilter={additionalFilter}
              customRenderOptions={(item, key, isSelected) => (
                <CheckBoxInputWithLabel
                  inputProps={{ value: isSelected, onChange: false }}
                  label={item?.name || ''}
                  key={key}
                />
              )}
              defaultOptions={getDefaultOptions()}
              removeSelected={removeSelected}
              addSelected={addSelected}
            />
          </Row>
          <Row>
            <FieldDTOWithCol
              control={control}
              component={TextInputWithLabel}
              dtoObject={attributes.rejection_reason_note}
              variant="material"
              inputClassName="custom-scroll-theme"
              withLabel
              disabled
              textarea
            />
          </Row>
          <Row>
            <FieldDTOWithCol
              control={control}
              component={TextInputWithLabel}
              dtoObject={attributes.additional_rejection_reason}
              variant="material"
              withLabel
              options={reasonListAll.map(i => ({ name: i.name, value: i.id }))}
              textarea
            />
          </Row>
        </div>
        <div className="application-action-content-rigth custom-scroll-theme">
          <div className="f3">Возможный список ошибок</div>
          <div className="application-action-content-info">
            <div className="application-action-content-info-icon">
              <Icons width={24} height={24} customIcon icon="violations" />
            </div>
            <div className="application-action-content-info-text">{formatMessage({ id: 'field_validation.denied.info' })}</div>
          </div>
          <div className="application-action-content-rigth-errors">
            {results.filter(item => item.status === CHECK_STATUS.FAILED).map((e, index) => (
              <div
                key={index}
                className={classNames('application-action-content-rigth-error', { selected: selected.filter(item => item.key === e.key).length })}
              >
                <div className="application-action-content-rigth-error-text">
                  {e.message}
                </div>
                {e?.ids && e.ids?.length
                  ? (
                    <div
                      className="application-action-content-rigth-error-selected"
                      onClick={() => {
                        const currentIds = getValues('rejection_reason_ids');
                        const isSelected = selected.some(item => item.key === e.key);

                        if (isSelected) {
                          setSelected(selected.filter(item => item.key !== e.key));
                          setValue('rejection_reason_ids', currentIds.filter(id => !e.ids.includes(id)));
                        } else {
                          setSelected([...selected, e]);
                          setValue('rejection_reason_ids', [...currentIds, ...e.ids]);
                        }
                      }}
                    >
                      Выбрать
                    </div>
                  ) : null}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
      <DialogFooter className="hff-footer">
        <div className="hff-footer-buttons">
          <Button className="hff-clear-data" variant="secondary" onClick={close}>Отменить</Button>
          <Button className="hff-clear-show" onClick={handleValidateAndAccept}>{buttonExecText}</Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
}
